import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Car,
  Bike,
  User,
  FileText,
  Check,
  Search,
  CheckCircle2,
  Upload,
  Phone,
  Mail,
  Camera,
  CreditCard,
  IdCard,
  Send,
  Clock,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { AnimatedTheGoodDealLogo } from "@/components/AnimatedLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo JYVIS
const JYVIS_LOGO = "/logo.svg";

// Contact Info
const contactInfo = {
  phone: "01 48 20 98 27",
  email: "contact@jyvis-assurances.com",
  whatsapp: "https://wa.me/message/XE3IM2IGGU5VE1",
};

// Social Media Links
const socialLinks = {
  instagram: "https://www.instagram.com/jyvis.assurances/",
  facebook: "https://www.facebook.com/profile.php?id=100078545120236",
  linkedin: "https://www.linkedin.com/in/jyvis-assurances-206017357/",
  twitter: "https://x.com/JyvisAssurances",
  youtube: "https://www.youtube.com/@JyvisAssurances",
  tiktok: "https://www.tiktok.com/@jyvis.assurances",
};

export default function DevisAuto() {
  const navigate = useNavigate();
  
  // Form state simplifié
  const [form, setForm] = useState({
    // Informations personnelles
    nom: "",
    prenom: "",
    dateNaissance: "",
    telephone: "",
    email: "",
    codePostal: "",
    ville: "",
    
    // Véhicule
    vehiculeType: "auto",
    plaque: "",
    
    // Préférence de rappel
    preferenceRappel: "matin",
  });
  
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [accepted, setAccepted] = useState(false);
  
  // Documents
  const [carteGrise, setCarteGrise] = useState(null);
  const [permisConduire, setPermisConduire] = useState(null);
  const [releveInfo, setReleveInfo] = useState(null);

  // Recherche plaque SIV
  const lookupPlate = async () => {
    if (!form.plaque || form.plaque.length < 7) {
      toast.error("Veuillez entrer une plaque valide (ex: AB-123-CD)");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vehicle/lookup/${form.plaque}`);
      setVehicleInfo(response.data);
      toast.success("Véhicule identifié !");
    } catch (error) {
      toast.error("Impossible d'identifier le véhicule");
      setVehicleInfo(null);
    } finally {
      setLoading(false);
    }
  };

  // Soumission du formulaire
  const handleSubmit = async () => {
    // Validation basique
    if (!form.nom || !form.prenom || !form.telephone || !form.email) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    if (!accepted) {
      toast.error("Veuillez accepter les conditions");
      return;
    }
    
    setLoading(true);
    try {
      // Simuler l'envoi
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSubmitted(true);
      toast.success("Demande envoyée ! Nous vous rappelons très vite.");
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Page de confirmation
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-3">Demande envoyée !</h1>
          <p className="text-gray-600 mb-6">
            Merci <strong>{form.prenom}</strong> ! Un conseiller Jyvis Assurance vous rappellera 
            <strong> {form.preferenceRappel === "matin" ? "ce matin" : form.preferenceRappel === "apres-midi" ? "cet après-midi" : "ce soir"}</strong> au 
            <strong> {form.telephone}</strong>.
          </p>
          <div className="flex flex-col gap-3">
            <Button onClick={() => navigate("/")} className="bg-[#102692] hover:bg-[#0D1F75]">
              Retour à l'accueil
            </Button>
            <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="text-[#102692] hover:underline flex items-center justify-center gap-2">
              <Phone className="w-4 h-4" />
              Appeler maintenant : {contactInfo.phone}
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#102692] text-white py-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img src={JYVIS_LOGO} alt="JYVIS" className="jyvis-logo h-10 w-auto" />
              <span className="font-bold text-lg">JYVIS</span>
            </Link>
            <div className="hidden sm:flex items-center gap-3">
              <div className="h-6 w-px bg-white/30" />
              <AnimatedTheGoodDealLogo size="sm" />
            </div>
          </div>
          <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full transition-colors">
            <Phone className="w-4 h-4" />
            <span className="hidden md:inline">{contactInfo.phone}</span>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          {/* Entête */}
          <div className="bg-gradient-to-r from-[#102692] to-[#1E3A8A] text-white p-6 text-center">
            <h1 className="text-2xl md:text-3xl font-bold flex items-center justify-center gap-3">
              {form.vehiculeType === "auto" ? <Car className="w-8 h-8" /> : <Bike className="w-8 h-8" />}
              Devis Auto & Moto
            </h1>
            <p className="text-blue-100 mt-2 text-lg">On vous rappelle au plus vite !</p>
            <div className="flex items-center justify-center gap-2 mt-3 text-sm text-blue-200">
              <Clock className="w-4 h-4" />
              <span>Réponse sous 30 minutes en moyenne</span>
            </div>
          </div>

          {/* Formulaire simplifié */}
          <div className="p-6 md:p-8 space-y-6">
            
            {/* Type de véhicule */}
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => updateForm("vehiculeType", "auto")}
                className={`flex items-center gap-3 px-6 py-4 rounded-xl border-2 transition-all ${
                  form.vehiculeType === "auto" 
                    ? "border-[#102692] bg-blue-50 text-[#102692]" 
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <Car className="w-6 h-6" />
                <span className="font-semibold">Auto</span>
              </button>
              <button
                onClick={() => updateForm("vehiculeType", "moto")}
                className={`flex items-center gap-3 px-6 py-4 rounded-xl border-2 transition-all ${
                  form.vehiculeType === "moto" 
                    ? "border-[#102692] bg-blue-50 text-[#102692]" 
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <Bike className="w-6 h-6" />
                <span className="font-semibold">Moto</span>
              </button>
            </div>

            {/* Plaque d'immatriculation */}
            <div className="bg-gray-50 rounded-xl p-5">
              <Label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-3">
                <Search className="w-4 h-4 text-[#F97316]" />
                Plaque d'immatriculation
              </Label>
              <div className="flex gap-2">
                <Input
                  value={form.plaque}
                  onChange={(e) => updateForm("plaque", e.target.value.toUpperCase())}
                  placeholder="AB-123-CD"
                  className="uppercase text-lg h-12 flex-1 text-center font-mono"
                  maxLength={9}
                  data-testid="input-plaque"
                />
                <Button 
                  onClick={lookupPlate} 
                  disabled={loading}
                  className="h-12 px-6 bg-[#102692] hover:bg-[#0D1F75]"
                >
                  {loading ? "..." : "Rechercher"}
                </Button>
              </div>
              
              {vehicleInfo && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200"
                >
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-semibold">{vehicleInfo.brand} {vehicleInfo.model}</span>
                    <span className="text-green-600">• {vehicleInfo.year}</span>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Informations de contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-700">Prénom *</Label>
                <Input
                  value={form.prenom}
                  onChange={(e) => updateForm("prenom", e.target.value)}
                  className="mt-1 h-12"
                  placeholder="Votre prénom"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">Nom *</Label>
                <Input
                  value={form.nom}
                  onChange={(e) => updateForm("nom", e.target.value)}
                  className="mt-1 h-12"
                  placeholder="Votre nom"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#F97316]" />
                  Téléphone *
                </Label>
                <Input
                  type="tel"
                  value={form.telephone}
                  onChange={(e) => updateForm("telephone", e.target.value)}
                  className="mt-1 h-12"
                  placeholder="06 12 34 56 78"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#F97316]" />
                  Email *
                </Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => updateForm("email", e.target.value)}
                  className="mt-1 h-12"
                  placeholder="votre@email.com"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">Code postal</Label>
                <Input
                  value={form.codePostal}
                  onChange={(e) => updateForm("codePostal", e.target.value)}
                  className="mt-1 h-12"
                  placeholder="93200"
                  maxLength={5}
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">Ville</Label>
                <Input
                  value={form.ville}
                  onChange={(e) => updateForm("ville", e.target.value)}
                  className="mt-1 h-12"
                  placeholder="Saint-Denis"
                />
              </div>
            </div>

            {/* Documents à joindre */}
            <div className="bg-blue-50 rounded-xl p-5">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#102692]" />
                Documents à joindre (optionnel)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Carte grise */}
                <div>
                  <label className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-white hover:bg-gray-50 transition-colors" data-testid="upload-carte-grise">
                    <CreditCard className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600 font-medium">Carte grise (PDF)</span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setCarteGrise(e.target.files[0])}
                    />
                  </label>
                  {carteGrise && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <Check className="w-3 h-3" /> {carteGrise.name}
                    </p>
                  )}
                </div>
                
                {/* Permis de conduire */}
                <div>
                  <label className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-white hover:bg-gray-50 transition-colors" data-testid="upload-permis">
                    <IdCard className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600 font-medium">Permis de conduire (PDF)</span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setPermisConduire(e.target.files[0])}
                    />
                  </label>
                  {permisConduire && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <Check className="w-3 h-3" /> {permisConduire.name}
                    </p>
                  )}
                </div>
                
                {/* Relevé d'information */}
                <div>
                  <label className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-[#F97316]/50 rounded-xl cursor-pointer bg-orange-50 hover:bg-orange-100 transition-colors" data-testid="upload-releve-info">
                    <FileText className="w-8 h-8 text-[#F97316] mb-2" />
                    <span className="text-sm text-[#F97316] font-medium">Relevé d'information</span>
                    <span className="text-xs text-orange-400">(Recommandé)</span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setReleveInfo(e.target.files[0])}
                    />
                  </label>
                  {releveInfo && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <Check className="w-3 h-3" /> {releveInfo.name}
                    </p>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3 text-center">
                Le relevé d'information permet d'obtenir un tarif plus avantageux
              </p>
            </div>

            {/* Préférence de rappel */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Quand souhaitez-vous être rappelé ?</Label>
              <div className="flex flex-wrap gap-3">
                {[
                  { value: "matin", label: "Ce matin", icon: "🌅" },
                  { value: "apres-midi", label: "Cet après-midi", icon: "☀️" },
                  { value: "soir", label: "Ce soir", icon: "🌙" },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateForm("preferenceRappel", option.value)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-all ${
                      form.preferenceRappel === option.value
                        ? "border-[#102692] bg-blue-50 text-[#102692]"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span>{option.icon}</span>
                    <span className="font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Conditions */}
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <Checkbox
                id="accept"
                checked={accepted}
                onCheckedChange={setAccepted}
                className="mt-1"
              />
              <label htmlFor="accept" className="text-sm text-gray-600 cursor-pointer">
                J'accepte d'être recontacté par Jyvis Assurance pour recevoir mon devis personnalisé.
              </label>
            </div>

            {/* Boutons d'action */}
            <div className="space-y-3">
              {/* Bouton WhatsApp - Rappel immédiat */}
              <a 
                href={contactInfo.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full h-14 bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold text-lg rounded-xl shadow-lg transition-all flex items-center justify-center gap-3"
                data-testid="whatsapp-btn"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Être rappelé tout de suite
              </a>
              
              {/* Séparateur */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-gray-400 text-sm">ou</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>
              
              {/* Bouton d'envoi classique */}
              <Button 
                onClick={handleSubmit}
                disabled={loading || !accepted}
                className="w-full h-14 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-lg rounded-xl shadow-lg transition-all"
                data-testid="submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Envoi en cours...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    Recevoir mon devis gratuit
                  </span>
                )}
              </Button>
            </div>

            {/* Rappel téléphone */}
            <div className="text-center">
              <p className="text-gray-500 text-sm mb-2">Vous préférez nous appeler ?</p>
              <a 
                href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} 
                className="inline-flex items-center gap-2 text-[#102692] font-semibold hover:underline"
              >
                <Phone className="w-5 h-5" />
                {contactInfo.phone}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
